import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import SearchBar from '@/components/feature/SearchBar';
import MediaGrid from '@/components/feature/MediaGrid';
import NewsCard from '@/pages/news/components/NewsCard';
import { useSeo } from '@/hooks/useSeo';
import { searchMedia, searchNews } from '@/utils/media';

export default function SearchPage() {
  const [params] = useSearchParams();
  const keyword = (params.get('q') ?? '').trim();

  useSeo({
    title: keyword ? `“${keyword}”的搜索结果 - 黄色影视` : '搜索影视 - 黄色影视',
    description: keyword
      ? `黄色影视中与“${keyword}”相关的影视作品与资讯搜索结果。`
      : '输入关键词，快速搜索黄色影视中的电影、电视剧、综艺与影视资讯。',
    keywords: keyword ? `${keyword},影视搜索,黄色影视` : '影视搜索,黄色影视',
  });

  const mediaResults = useMemo(() => searchMedia(keyword), [keyword]);
  const newsResults = useMemo(() => searchNews(keyword), [keyword]);
  const total = mediaResults.length + newsResults.length;

  return (
    <SiteLayout>
      <div className="mx-auto max-w-[1400px] px-4 py-8 md:px-6 md:py-12">
        <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
          <Link to="/" className="cursor-pointer transition-colors hover:text-primary-500">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">搜索</span>
        </nav>

        <header className="mt-4 border-b border-background-200/70 pb-6">
          <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">搜索影视内容</h1>
          <div className="mt-5 max-w-2xl">
            <SearchBar initialValue={keyword} size="lg" />
          </div>
          <p className="mt-4 text-sm text-foreground-600">
            {keyword ? (
              <>
                当前搜索词：<span className="font-semibold text-primary-500">“{keyword}”</span>，共找到{' '}
                <span className="font-semibold text-primary-500">{total}</span> 条结果
                {total > 0 ? `（影视 ${mediaResults.length} · 资讯 ${newsResults.length}）` : null}
              </>
            ) : (
              '输入电影、电视剧、综艺名称，或演员、导演、关键词，开始搜索。'
            )}
          </p>
        </header>

        {!keyword ? (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-background-200/70 bg-background-100 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-line text-2xl"></i>
            </span>
            <p className="mt-4 text-sm text-foreground-600">输入关键词开始搜索影视与资讯</p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {['悬疑', '爱情', '科幻', '综艺', '古装'].map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="cursor-pointer rounded-full bg-background-200/70 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </div>
        ) : total === 0 ? (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-background-200/70 bg-background-100 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </span>
            <p className="mt-4 text-sm text-foreground-600">没有找到与“{keyword}”相关的内容</p>
            <p className="mt-1 text-xs text-foreground-500">试试更换关键词，或返回首页浏览热门影视</p>
            <Link
              to="/"
              className="mt-5 flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          </div>
        ) : (
          <div className="mt-8 space-y-12">
            {mediaResults.length > 0 ? (
              <section>
                <h2 className="mb-5 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                  <i className="ri-film-line text-primary-500"></i>
                  影视作品
                  <span className="text-sm font-normal text-foreground-500">（{mediaResults.length}）</span>
                </h2>
                <MediaGrid items={mediaResults} showType />
              </section>
            ) : null}

            {newsResults.length > 0 ? (
              <section>
                <h2 className="mb-5 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                  <i className="ri-newspaper-line text-primary-500"></i>
                  影视资讯
                  <span className="text-sm font-normal text-foreground-500">（{newsResults.length}）</span>
                </h2>
                <div className="space-y-5">
                  {newsResults.map((item) => (
                    <NewsCard key={item.id} item={item} />
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        )}
      </div>
    </SiteLayout>
  );
}